import {
  PLATFORM_CONTENT_LIMITS,
  formatPlatformContentViolations,
  measureLength,
  resolvePlatformPublishText,
  validatePlatformContent as validateShared,
  type ContentLimitPlatform,
  type PlatformContentViolation,
} from '@mondaygpt/constants'
import { CreativeType } from '../types'

/**
 * Client preflight for the platform content constraints the server now enforces
 * on every schedule/publish path (see
 * `apps/server/src/creative/utils/post-content-validation.ts`).
 *
 * Same shared validator, same numbers, same copy — so a post the composer says
 * is fine is a post the server accepts, and a post the composer flags carries
 * the exact sentence the server would have returned. The point of running it
 * here is timing: the user finds out while they are still writing the post,
 * not hours later as a publish failure.
 */

const CREATIVE_TYPE_TO_PLATFORM: Record<CreativeType, ContentLimitPlatform> = {
  [CreativeType.InstagramPost]: 'instagram',
  [CreativeType.FacebookPost]: 'facebook',
  [CreativeType.TikTokVideo]: 'tiktok',
  [CreativeType.LinkedInPost]: 'linkedin',
  [CreativeType.XPost]: 'x',
  [CreativeType.YouTubeShort]: 'youtube_shorts',
  [CreativeType.PinterestPin]: 'pinterest',
}

export type { PlatformContentViolation }

export function creativeTypeToContentPlatform(type: CreativeType): ContentLimitPlatform | null {
  return CREATIVE_TYPE_TO_PLATFORM[type] ?? null
}

/** Violations for one creative. Empty when the post is publishable. */
export function platformContentViolations(creative: {
  type: CreativeType
  content: unknown
}): PlatformContentViolation[] {
  const platform = creativeTypeToContentPlatform(creative.type)
  if (!platform) return []
  const result = validateShared(platform, creative.content as Record<string, unknown>)
  return result.ok ? [] : result.violations
}

/** Violations across a post's selected platform variants, in the given order. */
export function platformContentViolationsForAll(
  creatives: ReadonlyArray<{ type: CreativeType; content: unknown }>
): PlatformContentViolation[] {
  return creatives.flatMap(platformContentViolations)
}

/** The message to show the user, or null when there is nothing to say. */
export function platformContentErrorMessage(
  creatives: ReadonlyArray<{ type: CreativeType; content: unknown }>
): string | null {
  const violations = platformContentViolationsForAll(creatives)
  return violations.length ? formatPlatformContentViolations(violations) : null
}

/**
 * Live counter state for a single editor field: the length as the provider
 * counts it, the cap, and whether the field is currently over.
 *
 * Counts the EFFECTIVE published text, not the raw box contents — on the
 * platforms that append the hashtag block to the body at publish (Instagram,
 * LinkedIn, TikTok, YouTube), the appended tags are part of what the provider
 * measures. A counter that ignored them would read green right up to a publish
 * rejection. It also uses each provider's own unit: code points for Pinterest
 * and Instagram, UTF-16 for TikTok, UTF-8 bytes for the YouTube description.
 *
 * Returns null when the platform publishes no constraint for that field, so an
 * editor can skip the counter rather than invent a number.
 */
export function fieldLengthState(
  type: CreativeType,
  contentField: string,
  content: unknown
): { count: number; max: number; unit: string; over: boolean } | null {
  const platform = creativeTypeToContentPlatform(type)
  if (!platform) return null
  const limit = PLATFORM_CONTENT_LIMITS[platform].fields.find(f => f.contentField === contentField)
  if (!limit) return null
  const text = resolvePlatformPublishText(
    platform,
    contentField,
    (content ?? {}) as Record<string, unknown>
  )
  const count = measureLength(text, limit.measure)
  return { count, max: limit.max, unit: limit.unit, over: count > limit.max }
}
